import React from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import * as constants from '@adc/constants'
import Debounce from './debounce'
import RequestManager from './requestManager'
import * as providers from './providers'
import Toggle from './toggle'
import {
  numbersOnly,
  getSendCodeErrorMessage,
  getVerifyCodeErrorMessage,
} from './utils'

/*
 * This container has all of the logic of phone verification.
 * Form-related props are expected to be received from redux-form or formik.
 * UI-related props are passed down to its children.
 */
export const PhoneVerificationContainer = props => (
  <Toggle>
    {({ isToggled, setToggle }) => (
      <RequestManager>
        {verifyCodeProps => (
          <RequestManager>
            {sendCodeProps => {
              const setToggleWithReset = isToggled => {
                setToggle(isToggled)
                sendCodeProps.reset()
                verifyCodeProps.reset()
                // When toggled, reset the phone input to the latestPhone and the code input to empty.
                props.reset()
                props.onToggle?.(isToggled)
              }

              const verifyCode = async code => {
                verifyCodeProps.setStart()

                try {
                  const response = props.userId
                    ? await props.verifyPhoneOTPForUser({
                        phoneNumber: numbersOnly(props.phoneValue),
                        otp: code,
                        userId: props.userId,
                      })
                    : await props.verifyPhoneOTP({
                        phoneNumber: numbersOnly(props.phoneValue),
                        otp: code,
                      })
                  setToggleWithReset(false)
                  props.onSuccess && props.onSuccess(response) // note: provider gives us data object (not body)
                } catch (e) {
                  verifyCodeProps.setError(e.body)
                }
              }

              const sendCode = async via => {
                verifyCodeProps.reset()

                props.change && props.change('code', '')
                props.untouch && props.untouch('code')

                sendCodeProps.setStart()

                try {
                  props.userId
                    ? await props.generatePhoneOTPForUser({
                        isStrict: props.isStrict,
                        phoneNumber: numbersOnly(props.phoneValue),
                        via,
                        userId: props.userId,
                      })
                    : await props.generatePhoneOTP({
                        isStrict: props.isStrict,
                        phoneNumber: numbersOnly(props.phoneValue),
                        via,
                      })

                  sendCodeProps.setSuccess()
                } catch (e) {
                  const errorTitle = _.get(e, 'body.title')
                  if (errorTitle === 'PHONE_ALREADY_VERIFIED') {
                    const dummyCode = '000000'
                    return verifyCode(dummyCode)
                  }
                  sendCodeProps.setError(e.body)
                }
              }

              const sendCodeViaSMS = () =>
                sendCode(constants.graph.enums.OTPDeliveryMechanism.SMS)
              const sendCodeViaCall = () =>
                sendCode(constants.graph.enums.OTPDeliveryMechanism.CALL)

              const sendCodeError = sendCodeProps.error
                ? getSendCodeErrorMessage(sendCodeProps.error.title)
                : null

              const verifyCodeError = verifyCodeProps.error
                ? getVerifyCodeErrorMessage(verifyCodeProps.error.title)
                : null

              const onChangePhoneInput = async e => {
                sendCodeProps.reset()
                verifyCodeProps.reset()
                props.change && props.change('code', '')
                props.untouch && props.untouch('code')
              }

              return (
                <Debounce fn={verifyCode}>
                  {debouncedFn => {
                    const onChangeCodeInput = e => {
                      verifyCodeProps.reset()
                      const code = e.target.value
                      if (code && code.length === 6) {
                        debouncedFn(code)
                      }
                    }
                    return props.children
                      ? props.children({
                          isCodeSent: sendCodeProps.success,
                          isSendingCode: sendCodeProps.loading,
                          isToggled,
                          isVerifyingCode: verifyCodeProps.loading,
                          latestPhone: props.latestPhone,
                          onChangeCodeInput,
                          onChangePhoneInput,
                          phoneError: props.phoneError,
                          phoneInputHelpText: props.phoneInputHelpText,
                          sendCodeError,
                          sendCodeProps,
                          sendCodeViaCall,
                          sendCodeViaSMS,
                          setToggleWithReset,
                          verifyCodeError,
                          verifyCodeProps,
                          compact: props.compact,
                          onDeleteClick: props.onDeleteClick,
                        })
                      : null
                  }}
                </Debounce>
              )
            }}
          </RequestManager>
        )}
      </RequestManager>
    )}
  </Toggle>
)

PhoneVerificationContainer.propTypes = {
  change: PropTypes.func.isRequired,
  children: PropTypes.func,
  generatePhoneOTP: PropTypes.func,
  generatePhoneOTPForUser: PropTypes.func,
  isStrict: PropTypes.bool,
  latestPhone: PropTypes.object,
  onSuccess: PropTypes.func,
  onToggle: PropTypes.func,
  phoneError: PropTypes.string,
  phoneInputHelpText: PropTypes.string,
  phoneValue: PropTypes.string,
  reset: PropTypes.func.isRequired,
  untouch: PropTypes.func.isRequired,
  verifyPhoneOTP: PropTypes.func,
  verifyPhoneOTPForUser: PropTypes.func,
  compact: PropTypes.bool,
  userId: PropTypes.string,
  onDeleteClick: PropTypes.func,
}

PhoneVerificationContainer.defaultProps = {
  generatePhoneOTP: providers.generatePhoneOTP,
  verifyPhoneOTP: providers.verifyPhoneOTP,
  generatePhoneOTPForUser: providers.generatePhoneOTPForUser,
  verifyPhoneOTPForUser: providers.verifyPhoneOTPForUser,
}

export default PhoneVerificationContainer
